import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

function Navbar() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tutorialDropdown, setTutorialDropdown] = useState(false);
  const [caseDropdown, setCaseDropdown] = useState(false);

  // Handle screen resize dynamically
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll helper
  const scrollToSection = (path, id) => {
    navigate(path);
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }, 50);
    setMenuOpen(false);
  };

  const navStyle = {
    backgroundColor: "#fdfffef5",
    boxShadow: "0 2px 8px rgba(236, 231, 231, 1)",
    position: "sticky",
    top: 0,
    zIndex: 1000,
    width: "100%",
  };

  const containerStyle = {
    maxWidth: "1200px",
    margin: "auto",
    padding: "12px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
  };

  const logoStyle = { height: "50px" };

  const menuStyle = {
    listStyle: "none",
    display: isMobile ? (menuOpen ? "flex" : "none") : "flex",
    flexDirection: isMobile ? "column" : "row",
    position: isMobile ? "absolute" : "static",
    top: isMobile ? "70px" : "auto",
    right: 0,
    background: isMobile ? "#e8ede9" : "transparent",
    width: isMobile ? "220px" : "auto",
    margin: 0,
    padding: isMobile ? "15px" : 0,
    borderRadius: isMobile ? "8px" : "0",
    boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.2)" : "none",
    transition: "all 0.3s ease",
    alignItems: isMobile ? "flex-start" : "center",
  };

  const menuItemStyle = {
    position: "relative",
    margin: isMobile ? "8px 0" : "0 0 0 18px",
  };

  const linkStyle = {
    textDecoration: "none",
    color: "black",
    fontSize: "14px",
    fontWeight: 500,
    padding: "6px 10px",
    display: "block",
    cursor: "pointer",
    borderRadius: "4px",
    transition: "all 0.3s ease",
  };

  const linkHover = {
    backgroundColor: "#545151ff",
    color: "white",
  };

  const dropdownStyle = {
    position: isMobile ? "static" : "absolute",
    top: isMobile ? "auto" : "100%",
    left: 0,
    background: "#f0efef",
    minWidth: "200px",
    boxShadow: isMobile ? "none" : "0 2px 8px rgba(0,0,0,0.2)",
    display: "none",
    flexDirection: "column",
    zIndex: 1000,
    borderRadius: "5px",
  };

  // Dropdown toggle logic
  const showDropdown = (e) => {
    if (!isMobile) e.currentTarget.querySelector(".dropdown").style.display = "flex";
  };
  const hideDropdown = (e) => {
    if (!isMobile) e.currentTarget.querySelector(".dropdown").style.display = "none";
  };
  const toggleDropdownMobile = (e) => {
    if (isMobile) {
      const dd = e.currentTarget.querySelector(".dropdown");
      dd.style.display = dd.style.display === "flex" ? "none" : "flex";
    }
  };

  // Hamburger button
  const toggleStyle = {
    display: isMobile ? "flex" : "none",
    flexDirection: "column",
    cursor: "pointer",
  };

  const barStyle = {
    height: "3px",
    width: "25px",
    background: "black",
    margin: "4px 0",
    transition: "0.3s",
  };

  // Link component with hover effect
  const HoverLink = ({ to, children, ...props }) => {
    const [hovered, setHovered] = useState(false);

    return (
      <Link
        to={to}
        style={hovered ? { ...linkStyle, ...linkHover } : linkStyle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        {...props}
      >
        {children}
      </Link>
    );
  };

  return (
    <>
      <nav style={navStyle}>
        <div style={containerStyle}>
          {/* Logo */}
          <Link to="/">
            <img src="/logo.png" alt="GeoFederal Logo" style={logoStyle} />
          </Link>

          {/* Hamburger */}
          <div style={toggleStyle} onClick={() => setMenuOpen(!menuOpen)}>
            <span style={barStyle}></span>
            <span style={barStyle}></span>
            <span style={barStyle}></span>
          </div>

          {/* Menu */}
          <ul style={menuStyle}>
            <li style={menuItemStyle}><HoverLink to="/">Home</HoverLink></li>

            {/* Geo Spatial Dropdown */}
            <li
              style={menuItemStyle}
              onMouseEnter={showDropdown}
              onMouseLeave={hideDropdown}
              onClick={toggleDropdownMobile}
            >
              <span style={linkStyle}>Geo Spatial ▾</span>
              <div className="dropdown" style={dropdownStyle}>
                <HoverLink to="/remote-sensing">Ceo Spatial Intelligence</HoverLink>
                <HoverLink to="/gis">GIS Service</HoverLink>
                <HoverLink to="/AI-ML">AI/ML</HoverLink>
                <HoverLink to="/data-visualization">Data Visualization</HoverLink>
                <HoverLink to="/mapping">Mapping</HoverLink>
              </div>
            </li>

            {/* MultiCloud Advisory Dropdown */}
            <li
              style={menuItemStyle}
              onMouseEnter={showDropdown}
              onMouseLeave={hideDropdown}
              onClick={toggleDropdownMobile}
            >
              <span style={linkStyle}>MultiCloud Advisory ▾</span>
              <div className="dropdown" style={dropdownStyle}>
                <HoverLink to="/cloud-one-migration">Cloud One Migration & Support</HoverLink>
                <HoverLink to="/govcloud-migration">GovCloud Migration & Support</HoverLink>
                <HoverLink to="/cloud-readiness">Cloud Readiness Assessment</HoverLink>
                <HoverLink to="/application-candidacy">Application Candidacy Assessment</HoverLink>
                <HoverLink to="/foundation-assessment">Foundation Assessment</HoverLink>
                <HoverLink to="/workload-readiness">Workload Migration Readiness</HoverLink>
              </div>
            </li>

            {/* Resources Dropdown with Tutorials & Case Studies */}
            <li
              style={menuItemStyle}
              onMouseEnter={showDropdown}
              onMouseLeave={hideDropdown}
              onClick={toggleDropdownMobile}
            >
              <span style={linkStyle}>Resources ▾</span>
              <div className="dropdown" style={dropdownStyle}>
                <HoverLink to="/support-downloads">Support & Downloads</HoverLink>

                {/* Nested Tutorials */}
                <div
                  style={{ position: "relative" }}
                  onMouseEnter={() => setTutorialDropdown(true)}
                  onMouseLeave={() => setTutorialDropdown(false)}
                  onClick={(e) => {
                    if (isMobile) {
                      e.stopPropagation();
                      setTutorialDropdown(!tutorialDropdown);
                    }
                  }}
                >
                  <span style={linkStyle}>Tutorials ▸</span>
                  {tutorialDropdown && (
                    <div
                      style={{
                        ...dropdownStyle,
                        position: isMobile ? "static" : "absolute",
                        top: 0,
                        left: isMobile ? 0 : "100%",
                        background: "#f0efef",
                        minWidth: "180px",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "5px",
                        zIndex: 1000,
                      }}
                    >
                      <HoverLink to="/tutorials/tuxedo-tutorial">Tuxedo Tutorial</HoverLink>
                      <HoverLink to="/tutorials/tibco">Tibco</HoverLink>
                      <HoverLink to="/tutorials/mq-series">MQ Series</HoverLink>
                      <HoverLink to="/tutorials/hp-nNonStop-tuxedo">HP NonStop Tuxedo</HoverLink>
                      <HoverLink to="/tutorials/E*Gate">E*Gate</HoverLink>
                    </div>
                  )}
                </div>

                {/* Nested Case Studies */}
                <div
                  style={{ position: "relative" }}
                  onMouseEnter={() => setCaseDropdown(true)}
                  onMouseLeave={() => setCaseDropdown(false)}
                  onClick={(e) => {
                    if (isMobile) {
                      e.stopPropagation();
                      setCaseDropdown(!caseDropdown);
                    }
                  }}
                >
                  <span style={linkStyle}>Case Studies ▸</span>
                  {caseDropdown && (
                    <div
                      style={{
                        ...dropdownStyle,
                        position: isMobile ? "static" : "absolute",
                        top: 0,
                        left: isMobile ? 0 : "100%",
                        background: "#f0efef",
                        minWidth: "180px",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "5px",
                        zIndex: 1000,
                      }}
                    >
                      <HoverLink to="/case-studies/customer-case-studies">Customer Case Studies</HoverLink>
                      <HoverLink to="/case-studies/eaivision-case-studies">EAIVision Case Studies</HoverLink>
                      <HoverLink to="/case-studies/technology-ideas">Technology Ideas</HoverLink>
                    </div>
                  )}
                </div>
              </div>
            </li>

            {/* Security Dropdown */}
            <li
              style={menuItemStyle}
              onMouseEnter={showDropdown}
              onMouseLeave={hideDropdown}
              onClick={toggleDropdownMobile}
            >
              <span style={linkStyle}>Security ▾</span>
              <div className="dropdown" style={dropdownStyle}>
                <HoverLink to="/IAM">(IAM)Identity And Access Management</HoverLink>
                <HoverLink to="/cloud-security">Cloud Security</HoverLink>
                <HoverLink to="/End-Point">End Point Security</HoverLink>
                <HoverLink to="/Instrusion">Instrusion Prevention And Detection</HoverLink>
              </div>
            </li>

            {/* Products Dropdown */}
            <li
              style={menuItemStyle}
              onMouseEnter={showDropdown}
              onMouseLeave={hideDropdown}
              onClick={toggleDropdownMobile}
            >
              <span style={linkStyle}>Products ▾</span>
              <div className="dropdown" style={dropdownStyle}>
                <HoverLink to="/product-A">EaiV</HoverLink>
                <HoverLink to="/product-B">EaiVTuxConnect</HoverLink>
                <HoverLink to="/product-C">EaiVTuxConnect</HoverLink>
                <HoverLink to="/product-D">EaiVTuxConnect</HoverLink>
              </div>
            </li>

            {/* Company Dropdown */}
            <li
              style={menuItemStyle}
              onMouseEnter={showDropdown}
              onMouseLeave={hideDropdown}
              onClick={toggleDropdownMobile}
            >
              <span style={linkStyle}>Company ▾</span>
              <div className="dropdown" style={dropdownStyle}>
                <a
                  href="#about"
                  style={linkStyle}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("/", "about");
                  }}
                >
                  About
                </a>
                <a
                  href="#partners"
                  style={linkStyle}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("/", "partners");
                  }}
                >
                  Partners
                </a>
                <HoverLink to="/contact">Contact Us</HoverLink>
              </div>
            </li>

            <li style={menuItemStyle}><HoverLink to="/careers">Careers</HoverLink></li>
          </ul>
        </div>
      </nav>

      {/* Floating Chatbot Button */}
      <button
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          backgroundColor: "#4dabf7",
          color: "white",
          border: "none",
          borderRadius: "50%",
          width: "60px",
          height: "60px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
          cursor: "pointer",
          fontSize: "24px",
          zIndex: 2000,
        }}
        onClick={() => alert("Chatbot coming soon! 🚀")}
      >
        💬
      </button>
    </>
  );
}

export default Navbar;
